import { Component, OnDestroy, inject } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { toSignal } from '@angular/core/rxjs-interop';
import { Chargepoint, ChargepointsApiService, ChargepointSearchCriteria, ChargepointStatus, ChargepointType } from '../../api/chargepoints-api.service';
import { distinctUntilChanged, debounceTime, mergeMap, Observable, finalize, Subscription, map, combineLatest, startWith } from 'rxjs';
import { CommonModule } from '@angular/common';
import { MatListModule } from '@angular/material/list';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { ChargepointCardComponent } from '../../components/chargepoints/chargepoint-card/chargepoint-card.component';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { Router, RouterModule } from '@angular/router';
import { RefreshService } from '../../components/common/refresh-widget/refresh.service';
import { PREDEFINED_FILTERS, PredefinedFilter } from './filter-options';
import { CreateFilterDialogComponent, CreateFilterDialogData, CreateFilterDialogResult } from '../../components/create-filter-dialog/create-filter-dialog.component';
import { ChargepointsSearchDialogComponent } from '../../components/chargepoints-search-dialog/chargepoints-search-dialog.component';

// Interfaces pour le système de filtrage avancé
export interface FilterCondition {
  field: string;
  operator: 'equals' | 'notEquals' | 'greaterThan' | 'lessThan' | 'greaterThanOrEqual' | 'lessThanOrEqual' | 'contains' | 'notContains' | 'startsWith' | 'endsWith' | 'in' | 'notIn' | 'between' | 'isNull' | 'isNotNull';
  value: any;
}

export interface FilterCriteria {
  operator: 'AND' | 'OR' | 'NOT';
  conditions: (FilterCondition | FilterCriteria)[];
}


const STATUS_ORDER = {
  [ChargepointStatus.AVAILABLE]: 0,
  [ChargepointStatus.CHARGING]: 1,
  [ChargepointStatus.PREPARING]: 2,
  [ChargepointStatus.FINISHING]: 3,
  [ChargepointStatus.CONFIGURING]: 4,
  [ChargepointStatus.UNREACHABLE]: 5,
};

const TYPE_ORDER = {
  [ChargepointType.OCPP]: 0,
  [ChargepointType.OKECHARGE]: 1,
  [ChargepointType.IVY]: 2,
  [ChargepointType.NETATMO]: 3,
};

@Component({
  selector: 'app-chargepoints-page',
  templateUrl: './chargepoints-page.component.html',
  styleUrls: ['./chargepoints-page.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatListModule,
    MatProgressBarModule,
    MatIconModule,
    MatToolbarModule,
    MatButtonModule,
    ChargepointCardComponent,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatTooltipModule,
    RouterModule,
  ],
  standalone: true
})
export class ChargepointsPageComponent implements OnDestroy {
  private readonly breakpointObserver = inject(BreakpointObserver);

  readonly isMobile = toSignal(
    this.breakpointObserver.observe(['(max-width: 599px)']).pipe(
      map((result) => result.matches)
    ),
    { initialValue: false }
  );

  public chargepoints$: Observable<Chargepoint[]>;
  public displayedChargepoints$: Observable<Chargepoint[]>;
  
  // Filtres prédéfinis importés depuis le fichier séparé
  public advancedFilterOptions = [
    {id: 'custom', name: 'Create your own filter', description: 'Create your own filter', criteria: null},
    {id: 'none', name: 'All chargepoints', description: 'Show all chargepoints without any filtering', criteria: null},
    ...PREDEFINED_FILTERS,
  ];

  // Clé pour le localStorage
  private readonly CUSTOM_FILTERS_KEY = 'chargepoints_custom_filters';

  public searchFormGroup = new FormGroup({
    type: new FormControl('OCPP'),
    id: new FormControl(''),
    orderBy: new FormControl('status'),
    reverse: new FormControl(false),
    advancedFilter: new FormControl('none'), // Filtre par défaut
  });

  public searching = false;

  private subscriptions: Subscription[] = [];

  constructor(
    private chargepointsApiService: ChargepointsApiService, 
    private router: Router, 
    private refreshService: RefreshService,
    private dialog: MatDialog
  ) {
    // Charger les filtres personnalisés depuis le localStorage
    this.loadCustomFilters();
    this.chargepoints$ = this.refreshService.refreshTrigger.pipe(
      mergeMap(() => this.doSearch())
    );
    this.subscriptions.push(
      this.searchFormGroup.valueChanges.pipe(
        map(values => `${values.type}:${values.id}`),
        debounceTime(1500),
        distinctUntilChanged(),
      ).subscribe(() => this.refreshService.trigger())
    );

    // Gérer la sélection du filtre 'custom'
    this.subscriptions.push(
      this.searchFormGroup.controls.advancedFilter.valueChanges.pipe(
        distinctUntilChanged()
      ).subscribe(filterId => {
        if (filterId === 'custom') {
          this.openCreateFilterDialog();
        }
      })
    );

    this.displayedChargepoints$ = combineLatest([
      this.chargepoints$,
      this.searchFormGroup.controls.orderBy.valueChanges.pipe(
        startWith(this.searchFormGroup.controls.orderBy.value ?? 'id')
      ),
      this.searchFormGroup.controls.reverse.valueChanges.pipe(
        startWith(this.searchFormGroup.controls.reverse.value ?? false)
      ),
      this.searchFormGroup.controls.advancedFilter.valueChanges.pipe(
        startWith(this.searchFormGroup.controls.advancedFilter.value ?? 'last_month_to_yesterday')
      )
    ]).pipe(
      map(([chargepoints, orderBy, reverse, advancedFilterId]) => {
        // Appliquer le filtre avancé
        const selectedFilter = this.advancedFilterOptions.find(f => f.id === advancedFilterId);
        const filterCriteria = selectedFilter?.criteria || null;
        let filteredChargepoints = this.applyAdvancedFilter(chargepoints, filterCriteria);

        // Appliquer le tri
        const orderedChargepoints = filteredChargepoints.sort((a, b) => {
          switch (orderBy) {
            case 'id':
              return b.id.localeCompare(a.id);
            case 'type':
              return (TYPE_ORDER[b.type ?? ''] ?? 0) - (TYPE_ORDER[a.type ?? ''] ?? 0);
            case 'status':
              return (STATUS_ORDER[b.status ?? ''] ?? 0) - (STATUS_ORDER[a.status ?? ''] ?? 0);
            case 'lastHeartbeat':
              return (b.lastHeartbeat?.getTime() ?? 0) - (a.lastHeartbeat?.getTime() ?? 0);
            default:
              return 0;
          };
        });
        if (reverse) {
          return orderedChargepoints.reverse();
        }
        return orderedChargepoints;
      })
    );
  }

  private applyAdvancedFilter(chargepoints: Chargepoint[], filterCriteria: FilterCriteria | null): Chargepoint[] {
    if (!filterCriteria) {
      return chargepoints;
    }

    // Protection contre les filtres trop complexes
    if (chargepoints.length > 1000) {
      console.warn('Large dataset detected, limiting filter performance');
    }

    return chargepoints.filter(chargepoint => {
      try {
        return this.evaluateFilterCriteria(chargepoint, filterCriteria);
      } catch (error) {
        console.error('Error evaluating filter criteria:', error);
        return false;
      }
    });
  }

  private evaluateFilterCriteria(chargepoint: Chargepoint, criteria: FilterCriteria, depth: number = 0): boolean {
    // Protection contre la récursion infinie
    if (depth > 10) {
      console.warn('FilterCriteria recursion depth exceeded, returning false');
      return false;
    }

    const results = criteria.conditions.map(condition => {
      if ('field' in condition) {
        // C'est une FilterCondition
        return this.evaluateCondition(chargepoint, condition);
      } else {
        // C'est une FilterCriteria imbriquée
        return this.evaluateFilterCriteria(chargepoint, condition, depth + 1);
      }
    });

    switch (criteria.operator) {
      case 'AND':
        return results.every(result => result);
      case 'OR':
        return results.some(result => result);
      case 'NOT':
        return !results[0]; // NOT ne s'applique qu'à une seule condition
      default:
        return true;
    }
  }

  private evaluateCondition(chargepoint: Chargepoint, condition: FilterCondition): boolean {
    let fieldValue = this.getFieldValue(chargepoint, condition.field);
    let conditionValue = condition.value;

    // Convert date fields to timestamps for comparison
    if (fieldValue instanceof Date) {
      fieldValue = fieldValue.getTime();
    } else if (typeof fieldValue === 'string' && !isNaN(Date.parse(fieldValue))) {
      fieldValue = new Date(fieldValue).getTime();
    }
    if (conditionValue instanceof Date) {
      conditionValue = conditionValue.getTime();
    } else if (typeof conditionValue === 'string' && !isNaN(Date.parse(conditionValue))) {
      conditionValue = new Date(conditionValue).getTime();
    }

    switch (condition.operator) {
      case 'equals':
        return fieldValue === conditionValue;
      case 'notEquals':
        return fieldValue !== conditionValue;
      case 'greaterThan':
        return fieldValue > conditionValue;
      case 'lessThan':
        return fieldValue < conditionValue;
      case 'greaterThanOrEqual':
        return fieldValue >= conditionValue;
      case 'lessThanOrEqual':
        return fieldValue <= conditionValue;
      case 'contains':
        return String(fieldValue).toLowerCase().includes(String(conditionValue).toLowerCase());
      case 'notContains':
        return !String(fieldValue).toLowerCase().includes(String(conditionValue).toLowerCase());
      case 'startsWith':
        return String(fieldValue).toLowerCase().startsWith(String(conditionValue).toLowerCase());
      case 'endsWith':
        return String(fieldValue).toLowerCase().endsWith(String(conditionValue).toLowerCase());
      case 'in':
        return Array.isArray(conditionValue) && conditionValue.includes(fieldValue);
      case 'notIn':
        return Array.isArray(conditionValue) && !conditionValue.includes(fieldValue);
      case 'isNull':
        return fieldValue === null || fieldValue === undefined;
      case 'isNotNull':
        return fieldValue !== null && fieldValue !== undefined;
      default:
        return true;
    }
  }

  private getFieldValue(chargepoint: Chargepoint, field: string): any {
    // Support pour les champs imbriqués (ex: "location.latitude")
    const fieldParts = field.split('.');
    let value: any = chargepoint;
    
    for (const part of fieldParts) {
      if (value && typeof value === 'object' && part in value) {
        value = value[part];
      } else {
        return undefined;
      }
    }
    
    return value;
  }

  private doSearch() {
    function toChargepointType(val?: string | null): ChargepointType | null {
      if (val && ['OCPP', 'OKECHARGE', 'IVY'].includes(val)) {
        return val as ChargepointType;
      }
      return null;
    }
    const searchCriteria: ChargepointSearchCriteria = {
      type: toChargepointType(this.searchFormGroup.value.type),
      id: this.searchFormGroup.value.id || null,
    };
    this.searching = true;
    return this.chargepointsApiService.searchChargepoints(searchCriteria).pipe(
      finalize(() => this.searching = false)
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  public goToChargepoint(id: string) {
    this.router.navigate(['/chargepoints', id]);
  }

  public openSearchDialog(): void {
    this.dialog.open(ChargepointsSearchDialogComponent, {
      width: '100vw',
      maxWidth: '100vw',
      height: '100vh',
      maxHeight: '100vh',
      panelClass: 'chargepoints-search-dialog-panel',
      autoFocus: 'first-titled-element',
      data: {
        searchFormGroup: this.searchFormGroup,
        advancedFilterOptions: this.advancedFilterOptions,
      },
    });
  }

  public getSearchSummary(): string {
    const { type, id, orderBy, reverse, advancedFilter } = this.searchFormGroup.value;
    const parts: string[] = [];

    parts.push(type === 'all' ? 'All types' : (type ?? 'OCPP'));

    if (id?.trim()) {
      parts.push(`ID: ${id.trim()}`);
    }

    const selectedFilter = this.advancedFilterOptions.find((filter) => filter.id === advancedFilter);
    if (selectedFilter && selectedFilter.id !== 'none') {
      parts.push(selectedFilter.name);
    }

    const orderLabels: Record<string, string> = {
      id: 'Identifier',
      type: 'Type',
      status: 'Status',
      lastHeartbeat: 'Last heartbeat',
    };
    const orderLabel = orderLabels[orderBy ?? 'status'] ?? orderBy ?? 'Status';
    if (!this.isMobile()) {
      parts.push(`${orderLabel} ${reverse ? '↓' : '↑'}`);
    } else {
      parts.push(orderLabel);
    }

    return parts.join(' · ');
  }

  // Charger les filtres personnalisés depuis le localStorage
  private loadCustomFilters(): void {
    try {
      const savedFilters = localStorage.getItem(this.CUSTOM_FILTERS_KEY);
      if (savedFilters) {
        const customFilters: PredefinedFilter[] = JSON.parse(savedFilters);
        // Ajouter les filtres personnalisés à la liste (après les filtres prédéfinis)
        this.advancedFilterOptions = [
          {id: 'custom', name: 'Create your own filter', description: 'Create your own filter', criteria: null},
          {id: 'none', name: 'All chargepoints', description: 'Show all chargepoints without any filtering', criteria: null},
          ...PREDEFINED_FILTERS,
          ...customFilters
        ];
        console.log('Loaded custom filters from localStorage:', customFilters);
      }
    } catch (error) {
      console.error('Error loading custom filters from localStorage:', error);
    }
  }

  // Sauvegarder les filtres personnalisés dans le localStorage
  private saveCustomFilter(filter: PredefinedFilter): void {
    try {
      const savedFilters = localStorage.getItem(this.CUSTOM_FILTERS_KEY);
      const customFilters: PredefinedFilter[] = savedFilters ? JSON.parse(savedFilters) : [];
      customFilters.push(filter);
      localStorage.setItem(this.CUSTOM_FILTERS_KEY, JSON.stringify(customFilters));
    } catch (error) {
      console.error('Error saving custom filters to localStorage:', error);
    }
  }

  // Ouvrir le dialog de création de filtre
  private openCreateFilterDialog(): void {
    const dialogRef = this.dialog.open<CreateFilterDialogComponent, CreateFilterDialogData, CreateFilterDialogResult>(
      CreateFilterDialogComponent,
      {
        width: '90vw',
        maxWidth: '1200px',
        height: '80vh',
        maxHeight: '800px',
        disableClose: false,
        data: {}
      }
    );

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.filter) {
        // Ajouter le nouveau filtre à la liste des options
        this.advancedFilterOptions.push(result.filter);
        
        // Sauvegarder dans le localStorage
        this.saveCustomFilter(result.filter);
        
        // Sélectionner automatiquement le nouveau filtre
        this.searchFormGroup.controls.advancedFilter.setValue(result.filter.id);
        
        console.log('New custom filter created and saved:', result.filter);
      } else {
        // Si l'utilisateur a annulé, remettre le filtre précédent
        this.searchFormGroup.controls.advancedFilter.setValue('none');
      }
    });
  }

  // Fonction pour copier le filtre dans le presse-papiers
  public async copyFilterToClipboard(): Promise<void> {
    const selectedFilterId = this.searchFormGroup.get('advancedFilter')?.value;
    const selectedFilter = this.advancedFilterOptions.find(f => f.id === selectedFilterId);
    
    if (!selectedFilter) {
      return;
    }

    let clipboardText = '';
    
    if (selectedFilter.criteria) {
      // Copier la structure JSON du filtre
      clipboardText = JSON.stringify(selectedFilter.criteria, null, 2);
    } else {
      // Pour le filtre "All chargepoints", copier juste le nom
      clipboardText = selectedFilter.name;
    }

    try {
      await navigator.clipboard.writeText(clipboardText);
      // Optionnel : afficher une notification de succès
      console.log('Filter copied to clipboard');
    } catch (err) {
      console.error('Failed to copy filter to clipboard:', err);
      // Fallback pour les navigateurs qui ne supportent pas l'API Clipboard
      this.fallbackCopyTextToClipboard(clipboardText);
    }
  }

  // Fallback pour les navigateurs qui ne supportent pas l'API Clipboard moderne
  private fallbackCopyTextToClipboard(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      console.log('Filter copied to clipboard (fallback)');
    } catch (err) {
      console.error('Fallback copy failed:', err);
    }
    
    document.body.removeChild(textArea);
  }

  // Fonction pour obtenir le texte descriptif du filtre sélectionné
  public getSelectedFilterText(): string {
    const selectedFilterId = this.searchFormGroup.get('advancedFilter')?.value;
    const selectedFilter = this.advancedFilterOptions.find(f => f.id === selectedFilterId);
    
    if (!selectedFilter || !selectedFilter.criteria) {
      return '';
    }

    const criteriaText = this.formatCriteriaAsText(selectedFilter.criteria);
    return `${selectedFilter.name}: ${criteriaText}`;
  }

  // Fonction pour formater les critères en texte lisible
  private formatCriteriaAsText(criteria: FilterCriteria): string {
    const conditions = criteria.conditions.map(condition => {
      if ('field' in condition) {
        // C'est une FilterCondition
        return this.formatConditionAsText(condition);
      } else {
        // C'est une FilterCriteria imbriquée
        return `(${this.formatCriteriaAsText(condition)})`;
      }
    });

    const operatorText = criteria.operator === 'AND' ? ' AND ' : 
                        criteria.operator === 'OR' ? ' OR ' : ' NOT ';
    
    return conditions.join(operatorText);
  }

  // Fonction pour formater une condition en texte lisible
  private formatConditionAsText(condition: FilterCondition): string {
    const fieldName = this.getFieldDisplayName(condition.field);
    const operatorText = this.getOperatorDisplayText(condition.operator);
    const valueText = this.formatValueForDisplay(condition.value);

    return `${fieldName} ${operatorText} ${valueText}`;
  }

  // Fonction pour obtenir le nom d'affichage d'un champ
  private getFieldDisplayName(field: string): string {
    const fieldNames: { [key: string]: string } = {
      'id': 'ID',
      'type': 'Type',
      'status': 'Status',
      'lastHeartbeat': 'Last Heartbeat',
      'chargePointVendor': 'Vendor',
      'chargePointModel': 'Model',
      'chargePointSerialNumber': 'Serial Number',
      'firmwareVersion': 'Firmware Version',
      'ssid': 'SSID',
      'version': 'Version',
      'chipId': 'Chip ID',
      'plugId': 'Plug ID',
      'plugName': 'Plug Name',
      'bridgeId': 'Bridge ID',
      'homeId': 'Home ID',
      'homeName': 'Home Name'
    };
    return fieldNames[field] || field;
  }

  // Fonction pour obtenir le texte d'affichage d'un opérateur
  private getOperatorDisplayText(operator: string): string {
    const operatorTexts: { [key: string]: string } = {
      'equals': '=',
      'notEquals': '≠',
      'greaterThan': '>',
      'lessThan': '<',
      'greaterThanOrEqual': '≥',
      'lessThanOrEqual': '≤',
      'contains': 'contains',
      'notContains': 'does not contain',
      'startsWith': 'starts with',
      'endsWith': 'ends with',
      'in': 'in',
      'notIn': 'not in',
      'isNull': 'is null',
      'isNotNull': 'is not null'
    };
    return operatorTexts[operator] || operator;
  }

  // Fonction pour formater une valeur pour l'affichage
  private formatValueForDisplay(value: any): string {
    if (value === null || value === undefined) {
      return 'null';
    }
    if (Array.isArray(value)) {
      return `[${value.join(', ')}]`;
    }
    if (typeof value === 'string' && value.includes('T') && value.includes('Z')) {
      // C'est probablement une date ISO
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
      }
    }
    return String(value);
  }

  // Méthode pour vérifier si un filtre est personnalisé (supprimable)
  public isCustomFilter(filterId: string): boolean {
    return filterId !== 'none' && 
           filterId !== 'custom' && 
           !PREDEFINED_FILTERS.some(predefined => predefined.id === filterId);
  }
}
